import React, { ReactNode, useMemo } from 'react';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { DescribedCost } from '@r1-frontend/ui-react/components/costs';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { TCharacteristicsProps } from '@r1-frontend/ui-react/components/List/Characteristics/Characteristics';
import Stepper from '@r1-frontend/ui-react/components/Stepper';
import { TSvgProps } from '@r1-frontend/ui-react/components/svg/createSvg';
import { IndentContainer, withRadius } from '@r1-frontend/ui-react/experimental/containers';
import SteppableCardFooter from '@r1-frontend/ui-react/organisms/Card/CardBricks/SteppableCardFooter';
import CardImage from '@r1-frontend/ui-react/organisms/Card/CardImage';
import * as S from '@r1-frontend/ui-react/organisms/Card/Router/styled';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import DesktopRouterCard from './Desktop';

export type TRouterCardCharacteristicsProps = TCharacteristicsProps & { Icon: (props: TSvgProps) => JSX.Element };

export type TRouterCardProps = {
    images: string[],
    title: string | JSX.Element,
    description: string | JSX.Element,

    labels?: ReactNode[],

    characteristics: TRouterCardCharacteristicsProps[],

    disabled?: boolean,
    isTestDrive?: boolean,

    cost: number,
    previousCost?: number,
    period: number,

    quantity?: number,

    btnText?: string,

    onButtonClick: () => void,
    onIncrement: () => void,
    onDecrement: () => void,
    onCostClick: () => void,

    className?: string,
}

const RouterCard = ({ isTestDrive = false, previousCost, ...props }: TRouterCardProps): JSX.Element => {
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);

    const desktopCharacteristics = useMemo(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        () => props.characteristics.map<TCharacteristicsProps>(({ Icon, ...item }) => ({ ...item })),
        [props.characteristics],
    );
    return (
        <div className={props.className} data-test={'RouterCard'}>
            <CardImage images={props.images} labels={props.labels}/>

            <IndentContainer $mobile={[4, 12, 12]} $desktop={[8, 24, 24]}>
                {isMobile
                    ? <SteppableCardFooter {...props} isTestDrive={isTestDrive} previousCost={previousCost} data-test={'RouterCard-mobile'}/>
                    : <DesktopRouterCard {...props} characteristics={desktopCharacteristics} data-test={'RouterCard-desktop'}/>
                }
            </IndentContainer>
            {!isMobile && (
                <IndentContainer $mobile={[4, 12, 12]} $desktop={[8, 24, 24]}>
                    <S.DesktopFooter>
                        <DescribedCost
                            data-test={'RouterCard_cost'}
                            onClick={props.quantity && props.quantity > 0 ? props.onCostClick : undefined}
                            installmentPeriod={props.period}
                            isTestDrive={isTestDrive}
                            previousCost={previousCost}
                        >
                            {props.cost}
                        </DescribedCost>
                        {props.quantity
                            ? !isTestDrive && (
                                <Stepper
                                    min={0}
                                    summaryLimitsReached={props.disabled}
                                    onIncrement={props.onIncrement}
                                    onDecrement={props.onDecrement}
                                    value={props.quantity}
                                    $bgColor={COLORS.ButtonPrimary}
                                    $color={COLORS.TextWhite}
                                />
                            )
                            : <Button
                                disabled={props.disabled}
                                bType={'secondary'}
                                size={'small'}
                                onClick={props.onButtonClick}
                                data-test={'RouterCard_onAdd'}
                            >
                                {props.btnText || 'Добавить'}
                            </Button>
                        }
                    </S.DesktopFooter>
                </IndentContainer>
            )}
        </div>
    );
};

export default withRadius<TRouterCardProps>(RouterCard, { $desktop: 32, $mobile: 24 });
